import { useEffect, useMemo, useRef, useState } from 'react';
import { starNews, socialNews, hotTags } from '@/mocks/gossip';

interface SearchHit {
  id: string;
  title: string;
  tag: string;
}

export default function Hero() {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [typed, setTyped] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const hits = useMemo<SearchHit[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const pool = [...starNews, ...socialNews];
    return pool
      .filter((it) => it.title.toLowerCase().includes(q) || it.tag.toLowerCase().includes(q))
      .slice(0, 6)
      .map((it) => ({ id: it.id, title: it.title, tag: it.tag }));
  }, [query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setFocused(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goTo = (id: string) => {
    setQuery('');
    setFocused(false);
    setTyped(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <section id="top" className="relative overflow-hidden bg-foreground-950">
      <div
        className="absolute inset-0 bg-cover bg-center object-top"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=artistic%20editorial%20collage%20of%20warm%20coral%20and%20cream%20tones%20with%20soft%20glowing%20spotlights%20film%20reels%20and%20floating%20news%20text%20shapes%2C%20abstract%20stylized%20entertainment%20magazine%20background%2C%20high%20contrast%20warm%20palette&width=1600&height=900&seq=hero-main&orientation=landscape)',
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-900/55 to-primary-950/85" aria-hidden="true" />

      <div className="relative mx-auto flex w-full max-w-[1320px] flex-col items-center px-4 pb-24 pt-36 text-center md:px-6 md:pb-32 md:pt-44">
        <span className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-4 py-1.5 text-xs font-medium tracking-wide text-background-50 backdrop-blur-sm">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400"></span>
          </span>
          娱乐热点资讯 · 7×24 更新 · 高清资源随心看
        </span>

        <h1 className="gossip-fade-up mt-7 font-heading text-4xl font-black leading-tight text-background-50 md:text-6xl md:leading-tight">
          吃瓜不迷路
          <br className="md:hidden" />
          <span className="md:mx-3" />
          热点零距离
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/80 md:text-base">
          吃瓜视频在线观看的一站式入口，明星八卦、社会热点、影视综艺一手速递。
          <span className="hidden sm:inline"> 热门瓜料实时更新，高清资源免费播放，点开就能刷、就能看个够。</span>
        </p>

        <div ref={wrapRef} className="mt-9 w-full max-w-2xl">
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              if (hits.length) goTo(hits[0].id);
            }}
            className="flex items-center gap-2 rounded-full border border-background-50/20 bg-background-50/95 p-2 pl-5 shadow-soft backdrop-blur"
          >
            <i className="ri-search-line shrink-0 text-foreground-500" style={{ width: 20, height: 20, fontSize: 20 }} />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setTyped(true);
              }}
              onFocus={() => setFocused(true)}
              placeholder="搜索想吃的瓜，如：音乐盛典、短剧出海…"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              aria-label="全站搜索"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              开始吃瓜
            </button>
          </form>

          {focused && typed && hits.length > 0 && (
            <div className="gossip-fade-up mt-3 overflow-hidden rounded-2xl border border-background-200 bg-background-50 text-left shadow-soft">
              <p className="border-b border-background-100 px-4 py-2 text-xs font-semibold text-foreground-500">
                为你找到 {hits.length} 条相关瓜料
              </p>
              <ul className="max-h-80 overflow-auto">
                {hits.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      onClick={() => goTo(hit.id)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-primary-50"
                    >
                      <span className="whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-700">
                        {hit.tag}
                      </span>
                      <span className="truncate text-sm text-foreground-800">{hit.title}</span>
                      <i className="ri-arrow-right-line ml-auto shrink-0 text-primary-500" style={{ width: 18, height: 18 }} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {focused && typed && query.trim() && hits.length === 0 && (
            <div className="gossip-fade-up mt-3 rounded-2xl border border-background-200 bg-background-50 px-4 py-3 text-left shadow-soft">
              <p className="text-sm text-foreground-600">暂未找到相关瓜料，换个关键词试试，或直接去“今日瓜榜”逛逛。</p>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <span className="text-xs font-medium text-background-50/60">大家都在搜：</span>
          {hotTags.slice(0, 8).map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => goTo(tag === '明星' || tag === '影视' || tag === '综艺' || tag === '音乐' ? 'star' : 'social')}
              className="whitespace-nowrap rounded-full border border-background-50/20 bg-background-50/10 px-3 py-1.5 text-xs text-background-50/90 backdrop-blur-sm transition-colors hover:border-accent-400 hover:text-accent-300"
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="mt-12 grid w-full max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { num: '120+', label: '日均瓜料更新' },
            { num: '8 大', label: '精品内容栏目' },
            { num: '7×24', label: '热点实时追踪' },
            { num: '免费', label: '高清随心看' },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-background-50/15 bg-background-50/10 px-4 py-3 text-center backdrop-blur-sm">
              <p className="font-heading text-xl font-bold text-accent-300 md:text-2xl">{s.num}</p>
              <p className="mt-0.5 text-xs text-background-50/70">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="relative h-6 bg-gradient-to-b from-primary-950/85 to-background-50" aria-hidden="true" />
    </section>
  );
}